import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

export interface Step {
  id: string;
  label: string;
}

interface StepperProps {
  steps: Step[];
  currentStep: string;
  className?: string;
}

export function Stepper({ steps, currentStep, className }: StepperProps) {
  const currentIndex = steps.findIndex((step) => step.id === currentStep);

  return (
    <nav aria-label="Progress" className={className}>
      <ol className="flex items-center gap-2">
        {steps.map((step, index) => {
          const isCompleted = index < currentIndex;
          const isCurrent = step.id === currentStep;

          return (
            <li key={step.id} className="flex items-center gap-2">
              <div className="flex items-center gap-2">
                <StepIndicator
                  stepNumber={index + 1}
                  isCompleted={isCompleted}
                  isCurrent={isCurrent}
                />
                <span
                  className={cn(
                    "text-sm font-medium",
                    isCurrent && "text-foreground",
                    isCompleted && "text-muted-foreground",
                    !isCurrent && !isCompleted && "text-muted-foreground/60"
                  )}
                >
                  {step.label}
                </span>
              </div>
              {index < steps.length - 1 && (
                <StepSeparator isCompleted={isCompleted} />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

interface StepIndicatorProps {
  stepNumber: number;
  isCompleted: boolean;
  isCurrent: boolean;
}

function StepIndicator({ stepNumber, isCompleted, isCurrent }: StepIndicatorProps) {
  if (isCompleted) {
    return (
      <div className="flex size-6 items-center justify-center rounded-full bg-primary text-primary-foreground">
        <Check className="size-3.5" />
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex size-6 items-center justify-center rounded-full text-xs font-semibold",
        isCurrent
          ? "bg-primary text-primary-foreground"
          : "border border-muted-foreground/30 text-muted-foreground/60"
      )}
    >
      {stepNumber}
    </div>
  );
}

interface StepSeparatorProps {
  isCompleted: boolean;
}

function StepSeparator({ isCompleted }: StepSeparatorProps) {
  return (
    <div
      className={cn(
        "h-px w-8",
        isCompleted ? "bg-primary" : "bg-muted-foreground/30"
      )}
    />
  );
}
